import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

interface ProvisionUsersScenarioProps {
  onBack: () => void;
}

const ProvisionUsersScenario: React.FC<ProvisionUsersScenarioProps> = ({ onBack }) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        Provision Users to an Application
      </Typography>
      
      <Typography variant="body1" paragraph>
        User provisioning is the process of assigning users to your application with specific roles that determine 
        their permissions and access levels. This scenario walks you through the complete process using AppConfig²'s 
        built-in user provisioning interface.
      </Typography>
      
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
          What You'll Learn
        </Typography>
        <Typography variant="body2">
          • How to assign individual users to applications with specific roles<br/>
          • How to manage existing user assignments and update their roles<br/>
          • How to use bulk provisioning for multiple users at once<br/>
          • How to verify role assignments appear in user tokens<br/>
          • Best practices for enterprise application user management
        </Typography>
      </Box>

      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
          📋 Prerequisites
        </Typography>
        <Typography variant="body2">
          • <strong>App Roles Defined:</strong> Your application must have app roles configured before you can assign them to users<br/>
          • <strong>User Accounts:</strong> Users must exist in your Entra ID tenant before they can be provisioned<br/>
          • <strong>Appropriate Permissions:</strong> You need Application Administrator or Global Administrator permissions<br/>
          • <strong>Enterprise Application:</strong> The application must have a service principal (enterprise application) in your tenant
        </Typography>
      </Box>
      
      <Stepper orientation="vertical">
        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Navigate to User Provisioning</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              First, let's access the user provisioning interface for your application:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Navigate to Applications section using the main sidebar" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Find and select your application from the list" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Select' to open the App Tester" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the App Tester sidebar, scroll down and click 'User Provisioning'" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
              <Typography variant="subtitle2" gutterBottom>
                What You'll See
              </Typography>
              <Typography variant="body2">
                The User Provisioning interface shows three main sections: currently provisioned users, 
                options to add new users, and bulk import capabilities.
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Review Currently Provisioned Users</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Before adding new users, let's understand the current state:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Expand the 'Provisioned Users' accordion if it's not already open" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Review the list of users currently assigned to this application" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Note each user's assigned role (shown as colored chips)" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Observe that users show their display name, UPN, and role assignment" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
              <Typography variant="subtitle2" gutterBottom>
                Understanding the User List
              </Typography>
              <Typography variant="body2">
                • <strong>Display Name:</strong> The user's full name from Entra ID<br/>
                • <strong>UPN:</strong> The user's login identifier (User Principal Name)<br/>
                • <strong>Role Chip:</strong> Shows the current role assignment for each user<br/>
                • <strong>Menu Button:</strong> Click the three dots (⋮) to edit or remove users
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Add a New User (Method 1: Manual Entry)</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Let's add a new user by manually entering their information:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Expand the 'Add New User' accordion" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the 'User Principal Name (UPN)' field, enter a user's email address" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the 'Select Role' dropdown, choose the role you want to assign" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Add User' to provision the user" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Watch for the success notification that appears" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Important Reminder
              </Typography>
              <Typography variant="body2">
                After adding users, you'll see a notification reminding you to click <strong>"Apply Changes"</strong> 
                to save your modifications to the application registration.
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Add a New User (Method 2: User Browser)</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Alternatively, you can browse and select users from your directory:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click the 'Browse' button next to the UPN field" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Wait for the user picker dialog to open" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Search for users by typing in the search field" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select a user from the results" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Submit' to populate the UPN field" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select a role and click 'Add User' as in Method 1" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#f3e5f5', borderRadius: 1, border: '1px solid #ce93d8' }}>
              <Typography variant="subtitle2" gutterBottom>
                🔍 User Browser Benefits
              </Typography>
              <Typography variant="body2">
                • <strong>Prevents typos:</strong> No need to manually type UPNs<br/>
                • <strong>Shows additional info:</strong> See display names, departments, and other details<br/>
                • <strong>Real-time search:</strong> Find users quickly with partial name matches<br/>
                • <strong>Validates existence:</strong> Only shows users that actually exist in your tenant
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Bulk Import Users (Method 3: JSON File)</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              For adding multiple users at once, you can use the bulk import feature:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the 'Add New User' section, find the 'Bulk Operations' area" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click the info icon (ℹ️) to view sample format" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Download Sample JSON' to get a template file" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Edit the sample file with your actual user UPNs and role IDs" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Import Users from JSON' and select your file" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Watch the console for import progress and any errors" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
              <Typography variant="subtitle2" gutterBottom>
                📄 JSON Format Example
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#fafafa', p: 1, borderRadius: 1 }}>
                {`[
    { "upn": "alice@contoso.com", "roleId": "reader-role-id" },
    { "upn": "bob@contoso.com", "roleId": "admin-role-id" }
  ]`}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Note:</strong> Role IDs can be found by looking at the dropdown options or using the sample file generator.
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Manage Existing User Assignments</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              You can also modify or remove existing user assignments:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the 'Provisioned Users' section, find the user you want to modify" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click the three dots (⋮) menu button on the right side" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select 'Edit Role' to change the user's role assignment" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Or select 'Remove' to deprovision the user from the application" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Watch for confirmation messages after each action" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Important: Removing vs Deleting
              </Typography>
              <Typography variant="body2">
                • <strong>Removing a user assignment</strong> only removes their access to this application<br/>
                • <strong>The user account itself</strong> is not deleted from your Entra ID tenant<br/>
                • <strong>The user can still sign in</strong> to other applications they have access to<br/>
                • <strong>Re-adding is easy</strong> - you can provision them again at any time
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Apply Changes to Save Modifications</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Don't forget to save your provisioning changes:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="After making any provisioning changes, look for reminder notifications" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click the 'Apply Changes' button to save your modifications" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Wait for the success confirmation message" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Verify that the provisioned users list reflects your changes" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
              <Typography variant="subtitle2" gutterBottom>
                💾 Why Apply Changes?
              </Typography>
              <Typography variant="body2">
                The "Apply Changes" step saves your provisioning modifications back to the application registration 
                in Microsoft Entra ID. Without this step, your changes exist only temporarily in AppConfig².
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>

        <Step active>
          <StepLabel>
            <Typography variant="subtitle1">Test Role Assignments in Tokens</Typography>
          </StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Finally, let's verify that role assignments work correctly:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Navigate to 'Fetch Access Token' in the App Tester sidebar" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Start Authentication' to sign in as one of the provisioned users" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Complete the authentication flow" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="In the 'Active Tokens' panel, examine the decoded ID token" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Look for the 'roles' claim - it should contain the user's assigned role" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Test with different users to see how role assignments vary" />
              </ListItem>
            </List>
            <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
              <Typography variant="subtitle2" gutterBottom>
                🎯 Expected Token Result
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#fafafa', p: 1, borderRadius: 1 }}>
                {`{
    "roles": ["Reader", "PowerUser"],
    "name": "Alice Smith",
    "upn": "alice@contoso.com",
    ...
  }`}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                The roles array should contain the exact role values you assigned to the user during provisioning.
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Finish</Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
      
      <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          User Provisioning Best Practices
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Define App Roles First" 
              secondary="Create and configure all necessary app roles before provisioning users to ensure consistent assignments" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Use Descriptive Role Names" 
              secondary="Choose clear, meaningful role names that reflect actual permissions (e.g., 'Reader', 'Administrator')" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Test Role Assignments" 
              secondary="Always verify that role claims appear correctly in tokens after provisioning users" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Use Bulk Import for Large Deployments" 
              secondary="For organizations with many users, prepare CSV/JSON files to streamline the provisioning process" 
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Common Issues & Solutions
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="User not found during provisioning" 
              secondary="Ensure the user exists in your Entra ID tenant and the UPN is spelled correctly" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Roles not appearing in tokens" 
              secondary="Check that app roles are defined, enabled, and properly assigned to users" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Bulk import failing" 
              secondary="Verify JSON format is correct and all role IDs exist in your application" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Permission denied errors" 
              secondary="Ensure you have Application Administrator or Global Administrator permissions" 
            />
          </ListItem>
        </List>
      </Box>

      <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Advanced Scenarios to Try
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Multi-Role Assignments" 
              secondary="Assign multiple roles to a single user to test complex permission scenarios" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Role Hierarchy Testing" 
              secondary="Create roles with different permission levels and test inheritance patterns" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Bulk Role Updates" 
              secondary="Practice updating multiple users' roles simultaneously using the bulk import feature" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="Guest User Provisioning" 
              secondary="Test provisioning external/guest users to understand different UPN formats" 
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to All Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default ProvisionUsersScenario;